/** Frontend API wrapper for workspace-scoped expense claims.
 *
 * Mirrors CashFlowPlansService: one static method per route, getAuthHeaders()
 * on every call, all errors surfaced via thrown Error.
 */

import { API_BASE, getAuthHeaders } from '../utils/supabase/client';
import type { ExpenseClaim, ExpenseStatus, ExpenseCategory } from '../types/accounting';

async function readError(res: Response): Promise<string> {
  try {
    const d = await res.json();
    return (d && (d.error || d.message)) || `HTTP ${res.status}`;
  } catch {
    return `HTTP ${res.status}`;
  }
}

export class ExpensesService {
  static async list(params: {
    workspaceId: string;
    status?: ExpenseStatus;
    category?: ExpenseCategory;
  }): Promise<ExpenseClaim[]> {
    const p = new URLSearchParams({ workspaceId: params.workspaceId });
    if (params.status) p.set('status', params.status);
    if (params.category) p.set('category', params.category);
    const res = await fetch(`${API_BASE}/expenses?${p}`, {
      headers: await getAuthHeaders(),
    });
    if (!res.ok) throw new Error(await readError(res));
    return res.json();
  }

  static async getById(id: string): Promise<ExpenseClaim> {
    const res = await fetch(`${API_BASE}/expenses/${encodeURIComponent(id)}`, {
      headers: await getAuthHeaders(),
    });
    if (!res.ok) throw new Error(await readError(res));
    return res.json();
  }

  static async create(data: Partial<ExpenseClaim>): Promise<ExpenseClaim> {
    const res = await fetch(`${API_BASE}/expenses`, {
      method: 'POST',
      headers: { ...(await getAuthHeaders()), 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error(await readError(res));
    return res.json();
  }

  static async update(id: string, data: Partial<ExpenseClaim>): Promise<ExpenseClaim> {
    const res = await fetch(`${API_BASE}/expenses/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      headers: { ...(await getAuthHeaders()), 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error(await readError(res));
    return res.json();
  }

  static async remove(id: string): Promise<void> {
    const res = await fetch(`${API_BASE}/expenses/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      headers: await getAuthHeaders(),
    });
    if (!res.ok) throw new Error(await readError(res));
  }

  static async approve(id: string): Promise<ExpenseClaim> {
    const res = await fetch(`${API_BASE}/expenses/${encodeURIComponent(id)}/approve`, {
      method: 'POST',
      headers: await getAuthHeaders(),
    });
    if (!res.ok) throw new Error(await readError(res));
    return res.json();
  }

  static async reject(id: string): Promise<ExpenseClaim> {
    const res = await fetch(`${API_BASE}/expenses/${encodeURIComponent(id)}/reject`, {
      method: 'POST',
      headers: await getAuthHeaders(),
    });
    if (!res.ok) throw new Error(await readError(res));
    return res.json();
  }

  /** Stores the claim's receipt: the body is the file and its Content-Type names the type. */
  static async attachReceipt(id: string, file: File): Promise<void> {
    const res = await fetch(`${API_BASE}/expenses/${encodeURIComponent(id)}/receipt`, {
      method: 'POST',
      headers: { ...(await getAuthHeaders()), 'Content-Type': file.type },
      body: file,
    });
    if (!res.ok) throw new Error(await readError(res));
  }

  /** A short-lived link to the claim's receipt. */
  static async receiptUrl(id: string): Promise<string> {
    const res = await fetch(`${API_BASE}/expenses/${encodeURIComponent(id)}/receipt`, {
      headers: await getAuthHeaders(),
    });
    if (!res.ok) throw new Error(await readError(res));
    const body = await res.json();
    if (typeof body?.url !== 'string') throw new Error('The receipt link could not be confirmed.');
    return body.url;
  }
}
